import {
  AimOutlined,
  BarsOutlined,
  CalendarOutlined,
  CrownFilled,
  DashboardOutlined,
  FieldTimeOutlined,
  LeftOutlined,
  MedicineBoxOutlined,
  PlusOutlined,
  ProjectOutlined,
  RightOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Flex,
  List,
  Progress,
  Row,
  Segmented,
  Space,
  Spin,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AllocationModal, { type AllocationDefaults } from '../../components/ResourcePlanning/AllocationModal';
import OverloadDrawer from '../../components/ResourcePlanning/OverloadDrawer';
import {
  bookedMinutes,
  colorOf,
  dailyCapacity,
  DATE,
  DEFAULT_DAILY_MINUTES,
  DISPLAY_DATE,
  homeTeam,
  hours,
  IconButton,
  membersOn,
  UNASSIGNED,
  useProjects,
  useTeams,
  utilizationStatus,
} from '../../components/ResourcePlanning/shared';
import type { Team } from '../../DTOS/ResourcePlanning';
import api from '../../services/resourcePlanning/ResourcePlanningService';

const monday = (date: Dayjs) => date.subtract((date.day() + 6) % 7, 'day').startOf('day');
const percent = (minutes: number, capacity: number) => (capacity ? Math.round((minutes * 100) / capacity) : 0);
const heat = (value: number, capacity: number) =>
  !capacity
    ? '#fafafa'
    : value > 100
      ? '#ff7875'
      : value >= 85
        ? '#95de64'
        : value >= 50
          ? '#bae0ff'
          : value > 0
            ? '#e6f4ff'
            : '#ffffff';
const LEGEND: [string, string][] = [
  ['sub 50%', '#e6f4ff'],
  ['50–84%', '#bae0ff'],
  ['85–100%', '#95de64'],
  ['peste 100%', '#ff7875'],
];

/** First screen for the technical director: where capacity is missing, where it is free and what needs fixing. */
const OverviewTab: React.FC = () => {
  const [, setParams] = useSearchParams();
  const [start, setStart] = useState(() => monday(dayjs()));
  const [weeks, setWeeks] = useState(4);
  const [allocating, setAllocating] = useState<AllocationDefaults | null>(null);
  const [fixing, setFixing] = useState(false);
  const { data: teams = [] } = useTeams();
  const { data: projects = [] } = useProjects();
  const from = start.format(DATE),
    to = start.add(weeks * 7 - 1, 'day').format(DATE);
  const {
    data: grid,
    isLoading,
    error,
  } = useQuery({ queryKey: ['resourcePlanning', 'grid', from, to], queryFn: () => api.grid(from, to) });
  const open = (params: Record<string, string>) => setParams({ tab: 'planning', ...params });

  const stats = useMemo(() => {
    const booked = bookedMinutes(grid);
    const active = teams.filter((team) => team.isActive).sort((a, b) => a.name.localeCompare(b.name));
    const periods = Array.from({ length: weeks }, (_, index) => {
      const first = start.add(index * 7, 'day');
      return { first, dates: Array.from({ length: 5 }, (__, day) => first.add(day, 'day').format(DATE)) };
    });
    const people = new Map<
      string,
      { userId: string; name: string; teamId: string | null; team: string; capacity: number; minutes: number }
    >();
    // Weekdays only: each home member adds the team's working time; booked time counts all projects.
    const rows = active.map((team) => ({
      team,
      cells: periods.map(({ dates }) =>
        dates.reduce(
          (cell, date) => {
            membersOn(team, date).forEach((member) => {
              const minutes = booked.get(`${member.userId}:${date}`) || 0;
              const capacity = dailyCapacity(teams, member.userId, date, grid?.dailyCapacities);
              cell.capacity += capacity;
              cell.minutes += minutes;
              const person = people.get(member.userId) || {
                userId: member.userId,
                name: member.userName,
                teamId: team.id,
                team: team.name,
                capacity: 0,
                minutes: 0,
              };
              person.capacity += capacity;
              person.minutes += minutes;
              person.teamId = team.id;
              person.team = team.name;
              people.set(member.userId, person);
            });
            return cell;
          },
          { minutes: 0, capacity: 0 },
        ),
      ),
    }));
    // Include allocated people without an active home team; they must not disappear from management totals.
    const resourceIds = new Set([...(grid?.allocations || []).map((row) => row.userId), ...(grid?.dailyCapacities || []).map(row => row.userId)]);
    const directTeam: Team = {
      id: UNASSIGNED,
      name: 'Resurse fara echipa de baza activa',
      isActive: true,
      leaderUserId: null,
      leaderName: null,
      dailyMinutes: DEFAULT_DAILY_MINUTES,
      memberships: [],
      rowVersion: 0,
    };
    const direct = {
      team: directTeam,
      cells: periods.map(({ dates }) =>
        dates.reduce(
          (cell, date) => {
            (grid?.people || [])
              .filter((person) => resourceIds.has(person.id) && !homeTeam(teams, person.id, date)?.isActive)
              .forEach((person) => {
                const capacity = dailyCapacity(teams, person.id, date, grid?.dailyCapacities),
                  minutes = booked.get(`${person.id}:${date}`) || 0;
                cell.capacity += capacity;
                cell.minutes += minutes;
                const item = people.get(person.id) || {
                  userId: person.id,
                  name: person.name,
                  teamId: null,
                  team: 'Fara echipa de baza activa',
                  capacity: 0,
                  minutes: 0,
                };
                item.capacity += capacity;
                item.minutes += minutes;
                people.set(person.id, item);
              });
            return cell;
          },
          { minutes: 0, capacity: 0 },
        ),
      ),
    };
    if (direct.cells.some((cell) => cell.capacity || cell.minutes)) rows.push(direct);
    const capacity = rows.reduce((total, row) => total + row.cells.reduce((sum, cell) => sum + cell.capacity, 0), 0);
    const allocated = rows.reduce((total, row) => total + row.cells.reduce((sum, cell) => sum + cell.minutes, 0), 0);
    const staffed = new Set((grid?.allocations || []).map((allocation) => allocation.projectId));
    const unstaffed = projects
      .filter(
        (project) =>
          project.isActive &&
          project.startDate <= to &&
          project.endDate >= from &&
          !staffed.has(project.id),
      )
      .sort((a, b) => a.startDate.localeCompare(b.startDate));
    const free = [...people.values()]
      .map((person) => ({ ...person, free: person.capacity - person.minutes }))
      .filter((person) => person.free > 0)
      .sort((a, b) => b.free - a.free || a.name.localeCompare(b.name));
    const overloads = (grid?.dailyLoads || [])
      .filter((load) => load.overloaded)
      .sort((a, b) => a.date.localeCompare(b.date) || a.userName.localeCompare(b.userName));
    return {
      periods,
      rows,
      capacity,
      allocated,
      unstaffed,
      free,
      overloads,
      freeTotal: free.reduce((sum, person) => sum + person.free, 0),
    };
  }, [grid, teams, projects, weeks, from]);

  const utilization = percent(stats.allocated, stats.capacity);
  const overloadedPeople = new Set(stats.overloads.map((load) => load.userId)).size;
  const kpis = [
    {
      title: 'Grad de utilizare',
      icon: <DashboardOutlined />,
      value: utilization,
      suffix: '%',
      color: utilization > 100 ? '#cf1322' : undefined,
      progress: true,
    },
    { title: 'Ore libere', icon: <FieldTimeOutlined />, value: +(stats.freeTotal / 60).toFixed(0), suffix: 'h' },
    {
      title: 'Depasiri',
      icon: <WarningOutlined />,
      value: stats.overloads.length,
      suffix: overloadedPeople ? `zile · ${overloadedPeople} pers.` : undefined,
      color: stats.overloads.length ? '#cf1322' : undefined,
    },
    {
      title: 'Proiecte fara oameni',
      icon: <ProjectOutlined />,
      value: stats.unstaffed.length,
      color: stats.unstaffed.length ? '#d46b08' : undefined,
    },
  ];
  const label = 220,
    cell = weeks > 8 ? 70 : 96;

  return (
    <Space direction='vertical' size='middle' style={{ width: '100%' }}>
      <Flex wrap gap={8} align='center'>
        <Segmented<number>
          value={weeks}
          onChange={setWeeks}
          options={[2, 4, 8, 12].map((value) => ({ value, label: `${value} sapt.` }))}
        />
        <Space.Compact>
          <IconButton
            title='Saptamana anterioara'
            icon={<LeftOutlined />}
            onClick={() => setStart(start.subtract(7, 'day'))}
          />
          <DatePicker
            picker='week'
            value={start}
            onChange={(date) => date && setStart(monday(date))}
            allowClear={false}
            format={(value) => `din ${value.format(DISPLAY_DATE)}`}
          />
          <IconButton
            title='Saptamana urmatoare'
            icon={<RightOutlined />}
            onClick={() => setStart(start.add(7, 'day'))}
          />
        </Space.Compact>
        <IconButton title='Saptamana curenta' icon={<AimOutlined />} onClick={() => setStart(monday(dayjs()))} />
        <Typography.Text type='secondary'>
          {dayjs(from).format(DISPLAY_DATE)} – {dayjs(to).format(DISPLAY_DATE)}
        </Typography.Text>
      </Flex>
      {error && <Alert type='error' showIcon message={(error as Error).message} />}

      <Spin spinning={isLoading}>
        <Space direction='vertical' size='middle' style={{ width: '100%' }}>
          <Row gutter={[12, 12]}>
            {kpis.map((kpi) => (
              <Col key={kpi.title} xs={12} lg={6}>
                <Card size='small'>
                  <Statistic
                    title={
                      <span>
                        {kpi.icon} {kpi.title}
                      </span>
                    }
                    value={kpi.value}
                    suffix={kpi.suffix}
                    valueStyle={{ color: kpi.color }}
                  />
                  {kpi.progress && (
                    <Progress
                      percent={Math.min(utilization, 100)}
                      showInfo={false}
                      size='small'
                      status={utilizationStatus(utilization)}
                    />
                  )}
                </Card>
              </Col>
            ))}
          </Row>

          <Card
            size='small'
            title={
              <span>
                <TeamOutlined /> Incarcarea echipelor pe saptamani
              </span>
            }
            extra={
              <Space wrap size={10}>
                {LEGEND.map(([text, color]) => (
                  <span key={text} style={{ fontSize: 12 }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        background: color,
                        marginRight: 4,
                      }}
                    />
                    {text}
                  </span>
                ))}
              </Space>
            }
          >
            {!stats.rows.length ? (
              <Empty description='Nicio echipa activa.' />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <div style={{ width: label + cell * weeks }}>
                  <div style={{ display: 'flex' }}>
                    <div style={{ width: label, minWidth: label }} />
                    {stats.periods.map(({ first }) => (
                      <div
                        key={first.valueOf()}
                        style={{ width: cell, minWidth: cell, textAlign: 'center', fontSize: 11, color: '#8c8c8c' }}
                      >
                        {first.format('DD.MM')} – {first.add(4, 'day').format('DD.MM')}
                      </div>
                    ))}
                  </div>
                  {stats.rows.map(({ team, cells }) => (
                    <div key={team.id} style={{ display: 'flex', alignItems: 'center' }}>
                      <div
                        style={{
                          width: label,
                          minWidth: label,
                          paddingRight: 8,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <strong>{team.name}</strong>
                        {team.leaderName && (
                          <Tooltip title={`Sef echipa: ${team.leaderName}`}>
                            <CrownFilled style={{ color: '#faad14', marginLeft: 6 }} />
                          </Tooltip>
                        )}
                      </div>
                      {cells.map((value, index) => {
                        const usage = percent(value.minutes, value.capacity),
                          first = stats.periods[index].first;
                        return (
                          <Tooltip
                            key={index}
                            title={
                              <>
                                <div>
                                  <strong>{team.name}</strong> · {first.format(DISPLAY_DATE)} –{' '}
                                  {first.add(4, 'day').format(DISPLAY_DATE)}
                                </div>
                                <div>
                                  {hours(value.minutes)} ocupate din {hours(value.capacity)}
                                </div>
                                <div style={{ opacity: 0.7 }}>Click pentru planificarea saptamanii</div>
                              </>
                            }
                          >
                            <div
                              onClick={() =>
                                open({
                                  date: first.format(DATE),
                                  range: 'week',
                                  teams: team.id === UNASSIGNED ? '' : team.id,
                                })
                              }
                              style={{
                                width: cell - 4,
                                minWidth: cell - 4,
                                margin: 2,
                                height: 34,
                                borderRadius: 4,
                                background: heat(usage, value.capacity),
                                border: '1px solid #f0f0f0',
                                cursor: 'pointer',
                                textAlign: 'center',
                                lineHeight: '32px',
                                fontWeight: 600,
                                fontSize: 13,
                                color: usage > 100 ? '#fff' : '#262626',
                              }}
                            >
                              {value.capacity ? `${usage}%` : '—'}
                            </div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={8}>
              <Card
                size='small'
                style={{ height: '100%' }}
                title={
                  <span>
                    <ProjectOutlined /> Proiecte fara oameni
                  </span>
                }
              >
                <List
                  size='small'
                  dataSource={stats.unstaffed}
                  locale={{ emptyText: 'Toate proiectele active au alocari.' }}
                  pagination={stats.unstaffed.length > 6 ? { pageSize: 6, size: 'small' } : false}
                  renderItem={(project) => {
                    const begin = project.startDate > from ? project.startDate : from,
                      end = project.endDate < to ? project.endDate : to;
                    return (
                      <List.Item
                        actions={[
                          <IconButton
                            key='gantt'
                            title='Vezi in Gantt'
                            size='small'
                            icon={<BarsOutlined />}
                            onClick={() =>
                              open({ view: 'gantt', range: 'fourWeeks', date: begin, projects: project.id })
                            }
                          />,
                          <IconButton
                            key='plan'
                            title='Aloca oameni'
                            size='small'
                            type='primary'
                            icon={<PlusOutlined />}
                            onClick={() => setAllocating({ projectId: project.id, from: begin, to: end })}
                          />,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={
                            <span
                              style={{
                                display: 'inline-block',
                                width: 4,
                                height: 32,
                                borderRadius: 2,
                                background: colorOf(project.id),
                              }}
                            />
                          }
                          title={
                            <Tooltip title={project.name}>
                              <span>
                                {project.number} · <Typography.Text type='secondary'>{project.name}</Typography.Text>
                              </span>
                            </Tooltip>
                          }
                          description={
                            <span style={{ fontSize: 12 }}>
                              <CalendarOutlined /> {dayjs(project.startDate).format(DISPLAY_DATE)} –{' '}
                              {dayjs(project.endDate).format(DISPLAY_DATE)}
                            </span>
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Card
                size='small'
                style={{ height: '100%' }}
                title={
                  <span>
                    <UserOutlined /> Persoane cu timp liber
                  </span>
                }
              >
                <List
                  size='small'
                  dataSource={stats.free.slice(0, 10)}
                  locale={{ emptyText: 'Nicio persoana cu timp liber.' }}
                  renderItem={(person) => (
                    <List.Item
                      actions={[
                        <IconButton
                          key='plan'
                          title='Aloca persoana'
                          size='small'
                          icon={<PlusOutlined />}
                          onClick={() => setAllocating({ userId: person.userId, teamId: person.teamId, from, to })}
                        />,
                      ]}
                    >
                      <div
                        style={{ width: '100%', cursor: 'pointer' }}
                        onClick={() =>
                          open({
                            date: from,
                            range: weeks > 4 ? 'quarter' : 'month',
                            view: 'load',
                            people: person.userId,
                          })
                        }
                      >
                        <Flex justify='space-between'>
                          <span>
                            {person.name}{' '}
                            <Typography.Text type='secondary' style={{ fontSize: 12 }}>
                              · {person.team}
                            </Typography.Text>
                          </span>
                          <Typography.Text type='success' style={{ fontSize: 12 }}>
                            {hours(person.free)} libere
                          </Typography.Text>
                        </Flex>
                        <Progress
                          percent={percent(person.minutes, person.capacity)}
                          size='small'
                          status={utilizationStatus(percent(person.minutes, person.capacity))}
                        />
                      </div>
                    </List.Item>
                  )}
                />
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Card
                size='small'
                style={{ height: '100%' }}
                title={
                  <span>
                    <WarningOutlined /> Depasiri de program
                  </span>
                }
                extra={
                  stats.overloads.length > 0 && (
                    <Button
                      size='small'
                      type='primary'
                      danger
                      icon={<MedicineBoxOutlined />}
                      onClick={() => setFixing(true)}
                    >
                      Rezolva
                    </Button>
                  )
                }
              >
                <List
                  size='small'
                  dataSource={stats.overloads.slice(0, 8)}
                  locale={{ emptyText: 'Nicio depasire. Programul este respectat.' }}
                  renderItem={(load) => (
                    <List.Item>
                      <Flex justify='space-between' style={{ width: '100%' }}>
                        <span>
                          {load.userName}{' '}
                          <Typography.Text type='secondary' style={{ fontSize: 12 }}>
                            · {dayjs(load.date).format('DD.MM')}
                          </Typography.Text>
                        </span>
                        <Tag color='red' bordered={false}>
                          {hours(load.minutes)} / {hours(load.capacity)}
                        </Tag>
                      </Flex>
                    </List.Item>
                  )}
                />
                {stats.overloads.length > 8 && (
                  <Typography.Text type='secondary' style={{ fontSize: 12 }}>
                    +{stats.overloads.length - 8} alte zile
                  </Typography.Text>
                )}
              </Card>
            </Col>
          </Row>
        </Space>
      </Spin>

      {allocating && (
        <AllocationModal
          key={JSON.stringify(allocating)}
          defaults={allocating}
          from={from}
          to={to}
          onClose={() => setAllocating(null)}
        />
      )}
      {!!grid?.conflicts?.length && (
        <Alert
          type='warning'
          showIcon
          message={`${grid.conflicts.length} suprapuneri orare in perioada selectata`}
          description={
            <Space wrap>
              {grid.conflicts.map((conflict, index) => (
                <Button
                  key={index}
                  size='small'
                  onClick={() => open({ date: conflict.date, range: 'day', people: conflict.userId, view: 'table' })}
                >
                  {conflict.userName} · {dayjs(conflict.date).format('DD.MM')} · {conflict.projectNumber} /{' '}
                  {conflict.otherProjectNumber}
                </Button>
              ))}
            </Space>
          }
        />
      )}
      {fixing && <OverloadDrawer warnings={stats.overloads} grid={grid} onClose={() => setFixing(false)} />}
    </Space>
  );
};
export default OverviewTab;
